<script setup lang="ts">
import type {
	BehaviorConfig,
	LocationDataConfig,
	NewSearchSuggestProps,
	StyleConfig,
	Suggestion
} from './types';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';

import {
	Command,
	CommandDialog,
	CommandList,
	CommandSeparator
} from '@/components/SearchSuggestRadixCommand';
import { DialogDescription } from '@/components/ui/dialog';
import { cn } from '@/utils';
import { VisuallyHidden } from '@blintz/radix-vue';
import { useFirstFieldSuggest } from '~/composables/useFirstFieldSuggest';
import { useNewSearchSuggest } from '~/composables/useNewSearchSuggest';
import { SEARCH_SUGGEST } from '~/constants';

import IconMaterialSymbolsLightArrowLeftAlt from '~icons/material-symbols-light/arrow-left-alt';
import IconMaterialSymbolsLightCancel from '~icons/material-symbols-light/cancel';

import ListingEmptyState from '../ListingEmptyState/EmptyState.vue';
import CodeGroup from './CodeGroup.vue';
import InsideLabelSuggestInput from './InsideLabelSuggestInput.vue';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';

interface FirstFieldSuggestDialogProps extends NewSearchSuggestProps {
	placeholderClasses?: string;
	locationData?: LocationDataConfig;
	styleConfig?: StyleConfig;
	behaviorConfig?: BehaviorConfig;
}

const props = withDefaults(defineProps<FirstFieldSuggestDialogProps>(), {
	isFetchingPropertyByCodeSearch: false,
	placeholders: () => ['Cidades e regiões'],
	showInputButton: true,
	locationData: () => ({}),
	styleConfig: () => ({}),
	behaviorConfig: () => ({
		enableAnimation: false,
		closeOnClickOutside: true,
		closeOnSelect: true,
		showCloseIcon: false
	})
});

const emit = defineEmits<{
	(e: 'select-code', value: Suggestion['searchParams']): void;
	(e: 'select-history', value: Suggestion): void;
	(e: 'update:search-term', value: string): void;
	(e: 'close-modal'): void;
}>();

const selectedLocation = defineModel<SelectAddressEvent | null>(
	'selectedLocation',
	{ default: null }
);

const isOpen = defineModel<boolean>('isOpen', { required: true });
const selectedValue = ref<SuggestionGroup>();

const {
	// State
	searchTerm,
	modelValue,
	commandEl,
	hasSelectedValue,
	// Computed
	searchByCodeOption,
	shouldShowEmptyState,
	locationAutocompleteData,
	loading,

	// Research suggestions
	filteredLastResearchs,
	removeResearchs,

	// Functions
	clear,
	handleKeyDown,
	getDisplayValue,
	handleApplySearchTerm,
	executeLocationAutocomplete
} = useNewSearchSuggest({
	props,
	emit: ((event: string, value?: any) => {
		if (event === 'select-code') {
			emit('select-code', value);
		} else if (event === 'update:search-term') {
			emit('update:search-term', value);
		} else if (event === 'close-modal') {
			emit('close-modal');
		}
	}) as any,
	isDropdownOpen: isOpen,
	providers: ['cities', 'macroregions']
});

// Filter suggestions for first field
const { filteredSuggestions } = useFirstFieldSuggest({
	searchTerm,
	locationAutocompleteData,
	filteredLastResearchs,
	searchByCodeOption
});

const { init: initGoogleMaps } = useGoogleMaps();

async function getLocation(placeId: string | null) {
	if (!placeId) return { lat: null, lng: null };

	// Lazily initialize Google Maps when needed
	const googleMapsApi = await initGoogleMaps();

	if (!googleMapsApi?.places) return { lat: null, lng: null };

	const { Place } = googleMapsApi.places;
	const place = new Place({ id: placeId });
	await place.fetchFields({ fields: ['location'] });

	if (!place.location) return { lat: null, lng: null };

	return {
		lat: place.location.lat(),
		lng: place.location.lng()
	};
}

function openDialog() {
	// Defer heavy modal rendering to next frame to reduce input delay (INP optimization)
	requestAnimationFrame(() => {
		isOpen.value = true;
		clear(false);

		if (searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
			executeLocationAutocomplete();
		}
	});
}

function closeDialog() {
	if (!modelValue.value && props.searchTerm) {
		handleApplySearchTerm();
	}
	isOpen.value = false;

	if (props.behaviorConfig.closeOnSelect ?? true) {
		emit('close-modal');
	}
}

async function handleModelValueUpdate(suggestion: Suggestion) {
	const { searchType, searchParams, type, macroregionUsed } = suggestion;

	if (type === 'research') {
		emit('select-history', suggestion);
		closeDialog();

		searchTerm.value = getDisplayValue(suggestion);
		hasSelectedValue.value = true;
		return;
	}

	// Code: emit and close
	if (searchType === 'code') {
		emit('select-code', searchParams);
		hasSelectedValue.value = true;
		closeDialog();
		return;
	}

	// Region/macroregion/city: set selection
	if (['region', 'macroregion', 'city'].includes(searchType)) {
		selectedLocation.value = {
			macroregions: searchParams.macroregions,
			cities: searchParams.cities,
			regions: searchParams.regions,
			states: searchParams.states,
			lat: searchParams.lat,
			lng: searchParams.lng,
			text: searchParams.text,
			placeId: searchParams.placeId,
			condoSlug: searchParams.condoSlug,
			code: searchParams.code,
			searchType: searchType,
			macroregionUsed
		};

		const displayText = getDisplayValue(suggestion);
		searchTerm.value = displayText;
		hasSelectedValue.value = true;

		closeDialog();
		return;
	}

	// Street
	if (searchType === 'street') {
		const { lat, lng } = await getLocation(searchParams.placeId ?? null);
		const displayText = getDisplayValue(suggestion);
		searchTerm.value = displayText;
		hasSelectedValue.value = true;

		selectedLocation.value = {
			macroregions: [],
			cities: [],
			regions: [],
			states: searchParams.states || ['sp'],
			lat: lat || searchParams.lat,
			lng: lng || searchParams.lng,
			text: displayText,
			placeId: searchParams.placeId,
			condoSlug: searchParams.condoSlug,
			code: searchParams.code,
			searchType: searchType,
			macroregionUsed
		};

		closeDialog();
		return;
	}

	// POI
	if (searchType === 'point_of_interest') {
		const { lat, lng } = await getLocation(searchParams.placeId ?? null);
		const displayText = getDisplayValue(suggestion);
		searchTerm.value = displayText;
		hasSelectedValue.value = true;

		selectedLocation.value = {
			macroregions: [],
			cities: [],
			regions: [],
			states: searchParams.states || ['sp'],
			lat: lat || searchParams.lat,
			lng: lng || searchParams.lng,
			text: displayText,
			placeId: searchParams.placeId,
			condoSlug: searchParams.condoSlug,
			code: searchParams.code,
			searchType: searchType,
			macroregionUsed
		};

		closeDialog();
		return;
	}

	// History (fallback for any remaining cases)
	emit('select-history', suggestion);
	closeDialog();
}

async function handleInputClear() {
	clear();
	selectedLocation.value = null;
	isOpen.value = true;

	if (searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
		executeLocationAutocomplete();
	}

	await nextTick();
}

const selectedDisplayText = computed<string>(() => {
	if (searchTerm.value) {
		return searchTerm.value;
	}
	return props.placeholders[0] || 'Cidades e regiões';
});

// Lifecycle
let savedScrollPosition = 0;
let originalHtmlOverflow = '';

function preventBodyScroll() {
	savedScrollPosition = window.scrollY;
	originalHtmlOverflow = document.documentElement.style.overflow;
	document.documentElement.style.overflow = 'hidden';
	document.body.classList.add('scroll-locked');
	document.body.style.top = `-${savedScrollPosition}px`;
}

function restoreBodyScroll() {
	document.documentElement.style.overflow = originalHtmlOverflow;
	document.body.classList.remove('scroll-locked');
	document.body.style.removeProperty('position');
	document.body.style.removeProperty('top');
	document.body.style.removeProperty('width');
	document.body.style.overflow = '';
	document.body.style.height = '';
	document.body.style.touchAction = '';
	requestAnimationFrame(() => {
		window.scrollTo(0, savedScrollPosition);
	});
}

const keyboardHeight = ref<number>(0);
const initialViewportHeight = ref<number>(0);

function handleKeyboardResize() {
	if (!window.visualViewport) return;
	const currentHeight = window.visualViewport.height;
	const heightDiff = initialViewportHeight.value - currentHeight;
	keyboardHeight.value = heightDiff > 150 ? heightDiff : 0;
}

function setupKeyboardHandling() {
	if (!window.visualViewport) return;
	initialViewportHeight.value = window.visualViewport.height;
	window.visualViewport.addEventListener('resize', handleKeyboardResize);
}

function cleanupKeyboardHandling() {
	if (!window.visualViewport) return;
	window.visualViewport.removeEventListener('resize', handleKeyboardResize);
	keyboardHeight.value = 0;
}

const inputEl =
	useTemplateRef<InstanceType<typeof InsideLabelSuggestInput>>('inputEl');

function focusInput() {
	inputEl.value?.focus();
}

watch(isOpen, newValue => {
	if (newValue) {
		preventBodyScroll();
		setupKeyboardHandling();
	} else {
		restoreBodyScroll();
		cleanupKeyboardHandling();
	}
});

onBeforeUnmount(() => {
	if (isOpen.value) {
		restoreBodyScroll();
	}
	cleanupKeyboardHandling();
});

function handleEnter(_: KeyboardEvent, selectedElement?: HTMLElement) {
	if (shouldShowEmptyState.value) {
		clear();
		return;
	}
	selectedElement?.click();
}

watch(
	() => props.isFetchingPropertyByCodeSearch,
	isFetching => {
		if (!isFetching) closeDialog();
	}
);

watch(searchTerm, searchTerm => emit('update:search-term', searchTerm));
</script>

<template>
	<div class="relative h-full max-w-full overflow-hidden">
		<!-- Trigger Button -->
		<div
			v-if="showInputButton"
			:class="cn('flex size-full items-center', inputWrapperClasses)"
		>
			<slot name="input-prefix" />
			<button
				:class="
					cn(
						'flex h-[50px] w-full items-center justify-between rounded-tl-[2px] rounded-tr-[2px] border-b border-neutral-200 px-4 py-2 text-sm font-normal leading-normal',
						buttonInputClasses || 'bg-white'
					)
				"
				@click="openDialog"
			>
				<span
					:class="
						cn(
							'flex-1 truncate text-left',
							searchTerm ? 'text-primary' : 'text-primary-light-1',
							placeholderClasses
						)
					"
				>
					{{ selectedDisplayText }}
				</span>
				<div
					:class="
						cn(
							'flex h-full w-7 items-center justify-end',
							inputClearIconWrapperClasses
						)
					"
				>
					<IconMaterialSymbolsLightCancel
						v-show="searchTerm"
						:class="
							cn(
								'size-5 min-w-5 cursor-pointer text-white-dark-3',
								inputClearIconClasses
							)
						"
						@click.stop="handleInputClear"
					/>
				</div>
			</button>
		</div>

		<!-- Modal -->
		<Transition name="slide-up">
			<div v-if="isOpen" class="absolute left-0 top-0 h-dvh w-dvw max-w-none">
				<CommandDialog
					:open="isOpen"
					class="h-dvh w-dvw max-w-none"
					:show-close-icon="false"
					title="Buscar por cidade ou região"
					hide-title
					@update:open="isOpen = $event"
				>
					<div ref="commandEl" class="h-dvh w-dvw bg-white">
						<Command
							v-model="modelValue"
							v-model:search-term="searchTerm"
							v-model:selected-value="selectedValue"
							:display-value="getDisplayValue"
							reset-search-term-on-blur
							reset-search-term-on-select
							class="h-dvh w-full bg-white"
							@update:model-value="
								event => handleModelValueUpdate(event as Suggestion)
							"
							@keydown="handleKeyDown"
						>
							<InsideLabelSuggestInput
								ref="inputEl"
								placeholder="Cidades, regiões e código do imóvel"
								:floating-label="false"
								:class="
									cn(
										'h-[62px] text-ellipsis pl-2.5 text-primary text-base!',
										inputClasses
									)
								"
								:auto-focus="true"
								:model-value="searchTerm"
								:enable-animation="false"
								:show-close-icon="false"
								@clear="handleInputClear"
								@enter="handleEnter"
							>
								<template #prefix>
									<div class="ml-5 flex size-6 items-center justify-center">
										<IconMaterialSymbolsLightArrowLeftAlt
											class="size-full h-6! w-6! text-primary"
											@click="closeDialog"
										/>
									</div>
								</template>
							</InsideLabelSuggestInput>
							<CommandSeparator class="bg-white-dark-2" />

							<CommandList
								:class="
									cn(
										'h-full max-h-none border border-t-0 border-white-dark-2 bg-white p-0 pb-4 transition-all duration-300 ease-out',
										loading ? 'overflow-hidden!' : 'overflow-y-auto'
									)
								"
								:style="{ paddingBottom: `${keyboardHeight + 16}px` }"
								data-scroll-area
							>
								<div v-show="shouldShowEmptyState" class="px-5 py-6">
									<ListingEmptyState
										:show-badges="false"
										icon-wrapper-classes="size-[47.5px]"
										icon-classes="size-[22.5px]"
										title-classes="text-lg"
										description-classes="text-sm"
									>
										<template #title>Nenhum resultado encontrado</template>
										<template #description>
											Tente uma busca diferente ou verifique a grafia
										</template>
									</ListingEmptyState>
								</div>

								<LoadingGroup
									v-for="index in 3"
									:key="`loading-group-${index}`"
									:active="loading"
								/>

								<template v-if="!loading && filteredSuggestions?.length">
									<template
										v-for="(suggestionGroup, index) in filteredSuggestions"
										:key="`suggestion-group-${suggestionGroup.key}`"
									>
										<CommandSeparator
											class="bg-white-dark-2"
											:class="{
												hidden: !suggestionGroup.visible || index === 0
											}"
										/>

										<NewGroup
											v-if="suggestionGroup.key === 'lastResearchs'"
											:suggestion-key="'lastResearchs'"
											:heading="suggestionGroup.heading"
											:suggestions="suggestionGroup.suggestions"
											:search-term="searchTerm"
											is-removable
											@remove-suggestion="removeResearchs"
										/>

										<NewGroup
											v-else-if="suggestionGroup.key !== 'code'"
											:suggestion-key="suggestionGroup.key"
											:heading="suggestionGroup.heading"
											:suggestions="suggestionGroup.suggestions"
											:search-term="searchTerm"
										/>

										<CodeGroup
											v-else-if="suggestionGroup.key === 'code'"
											:search-term="searchTerm"
											suggestion-key="code"
											:suggestions="suggestionGroup.suggestions as any"
											:is-fetching-property-by-code-search="
												isFetchingPropertyByCodeSearch
											"
										/>
									</template>
								</template>
							</CommandList>
						</Command>

						<VisuallyHidden>
							<DialogDescription />
						</VisuallyHidden>
					</div>
				</CommandDialog>
			</div>
		</Transition>
	</div>
</template>
<!-- Styles moved to app/assets/css/main.css to avoid render-blocking CSS -->
